import { useMemo, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

/** Symbol entry from proto-index.json */
export interface SymbolEntry {
  file: string;
  line: number;
  kind: string;
}

export interface ProtoViewerProps {
  content: string;
  filePath: string;
  symbols: Record<string, SymbolEntry>;
  currentPackage: string;
  /** FQN of symbol to highlight (e.g. from URL fragment) */
  highlightSymbol?: string;
  /** When true, only render the highlighted symbol's lines */
  showOnlyHighlighted?: boolean;
}

const KEYWORDS = new Set([
  'syntax',
  'package',
  'import',
  'message',
  'service',
  'enum',
  'rpc',
  'returns',
  'oneof',
  'map',
  'repeated',
  'optional',
  'reserved',
  'option',
  'extend',
  'stream',
  'required',
  'extensions',
  'public',
  'weak',
]);

const PRIMITIVES = new Set([
  'string',
  'int32',
  'int64',
  'uint32',
  'uint64',
  'sint32',
  'sint64',
  'fixed32',
  'fixed64',
  'sfixed32',
  'sfixed64',
  'bool',
  'float',
  'double',
  'bytes',
]);

const useStyles = makeStyles({
  root: {
    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    backgroundColor: '#282c34',
    color: '#abb2bf',
    padding: '16px',
    borderRadius: '4px',
    overflow: 'auto',
    margin: 0,
  },
  lineNumber: {
    display: 'inline-block',
    width: '45px',
    textAlign: 'right',
    paddingRight: '16px',
    marginRight: '16px',
    borderRight: '1px solid #3e4451',
    color: '#4b5263',
    userSelect: 'none',
  },
  line: {
    whiteSpace: 'pre',
    '&:hover': {
      backgroundColor: '#2c313a',
    },
  },
  lineHighlighted: {
    whiteSpace: 'pre',
    backgroundColor: 'rgba(97, 175, 239, 0.1)',
    borderLeft: '3px solid #61afef',
    paddingLeft: '0px',
    '&:hover': {
      backgroundColor: 'rgba(97, 175, 239, 0.15)',
    },
  },
  keyword: { color: '#c678dd' },
  primitive: { color: '#e5c07b' },
  string: { color: '#98c379' },
  comment: { color: '#5c6370', fontStyle: 'italic' },
  number: { color: '#d19a66' },
  typeRef: {
    color: '#61afef',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  punctuation: { color: '#abb2bf' },
  fieldName: { color: '#e06c75' },
});

interface Token {
  type:
    | 'keyword'
    | 'primitive'
    | 'string'
    | 'comment'
    | 'number'
    | 'typeRef'
    | 'punctuation'
    | 'fieldName'
    | 'text'
    | 'importPath';
  value: string;
  fqn?: string;
  targetFile?: string;
}

/**
 * Resolve a type reference to a fully-qualified name in the index.
 * Tries: currentPackage.name, then name as-is (fully qualified).
 */
function resolveType(
  name: string,
  currentPkg: string,
  symbols: Record<string, SymbolEntry>,
): string | undefined {
  if (PRIMITIVES.has(name) || KEYWORDS.has(name)) return undefined;
  // Try fully qualified with current package
  const fqn = `${currentPkg}.${name}`;
  if (symbols[fqn]) return fqn;
  // Try as fully qualified name directly
  if (symbols[name]) return name;
  // Try with dots (e.g. nested.Type -> pkg.nested.Type)
  if (name.includes('.')) {
    const withPkg = `${currentPkg}.${name}`;
    if (symbols[withPkg]) return withPkg;
  }
  return undefined;
}

function tokenizeLine(
  line: string,
  currentPkg: string,
  symbols: Record<string, SymbolEntry>,
  inBlockComment: boolean,
): { tokens: Token[]; stillInBlockComment: boolean } {
  const tokens: Token[] = [];
  let pos = 0;
  let blockComment = inBlockComment;

  const remaining = () => line.slice(pos);
  const push = (type: Token['type'], value: string, extra?: Partial<Token>) => {
    tokens.push({ type, value, ...extra });
    pos += value.length;
  };

  if (blockComment) {
    const endIdx = line.indexOf('*/');
    if (endIdx === -1) {
      push('comment', line);
      return { tokens, stillInBlockComment: true };
    }
    push('comment', line.slice(0, endIdx + 2));
    blockComment = false;
  }

  while (pos < line.length) {
    const rest = remaining();

    // Block comment start
    if (rest.startsWith('/*')) {
      const endIdx = rest.indexOf('*/', 2);
      if (endIdx === -1) {
        push('comment', rest);
        return { tokens, stillInBlockComment: true };
      }
      push('comment', rest.slice(0, endIdx + 2));
      continue;
    }

    // Line comment
    if (rest.startsWith('//')) {
      push('comment', rest);
      break;
    }

    // Strings
    if (rest[0] === '"' || rest[0] === "'") {
      const quote = rest[0];
      let end = 1;
      while (end < rest.length && rest[end] !== quote) {
        if (rest[end] === '\\') end++;
        end++;
      }
      if (end < rest.length) end++;
      const strValue = rest.slice(0, end);

      // Check if this is an import path
      const importIdx = tokens.findIndex(
        t => t.type === 'keyword' && t.value === 'import',
      );
      if (importIdx !== -1) {
        const pathContent = strValue.slice(1, -1);
        push('importPath', strValue, { targetFile: pathContent });
      } else {
        push('string', strValue);
      }
      continue;
    }

    // Numbers
    const numMatch = rest.match(/^-?\d+(\.\d+)?([eE][+-]?\d+)?/);
    if (numMatch && (pos === 0 || /[\s=\[,(]/.test(line[pos - 1]))) {
      push('number', numMatch[0]);
      continue;
    }

    // Punctuation
    if ('{}()[];=,<>'.includes(rest[0])) {
      push('punctuation', rest[0]);
      continue;
    }

    // Whitespace
    const wsMatch = rest.match(/^\s+/);
    if (wsMatch) {
      push('text', wsMatch[0]);
      continue;
    }

    // Identifiers / keywords
    const idMatch = rest.match(/^[a-zA-Z_][a-zA-Z0-9_.]*/)
    if (idMatch) {
      const word = idMatch[0];
      if (KEYWORDS.has(word)) {
        push('keyword', word);
      } else if (PRIMITIVES.has(word)) {
        push('primitive', word);
      } else {
        // Determine if this is a type reference or field name
        // Type refs: start with uppercase or contain dots
        const isTypeCandidate =
          /^[A-Z]/.test(word) || word.includes('.');
        const resolved = isTypeCandidate
          ? resolveType(word, currentPkg, symbols)
          : undefined;

        if (resolved) {
          const entry = symbols[resolved];
          push('typeRef', word, {
            fqn: resolved,
            targetFile: entry.file,
          });
        } else if (/^[a-z_]/.test(word) && !word.includes('.')) {
          push('fieldName', word);
        } else {
          push('text', word);
        }
      }
      continue;
    }

    // Fallback: single character
    push('text', rest[0]);
  }

  return { tokens, stillInBlockComment: blockComment };
}

/**
 * Build a map of line numbers to FQNs for anchor IDs.
 * Only for message/service/enum definitions on that line.
 */
function buildLineAnchors(
  symbols: Record<string, SymbolEntry>,
  filePath: string,
): Map<number, string> {
  const map = new Map<number, string>();
  for (const [fqn, entry] of Object.entries(symbols)) {
    if (
      entry.file === filePath &&
      (entry.kind === 'message' || entry.kind === 'service' || entry.kind === 'enum')
    ) {
      map.set(entry.line, fqn);
    }
  }
  return map;
}

/**
 * Compute the line range (1-based, inclusive) for a symbol's block.
 * Starts at the definition line and ends when braces balance.
 */
function computeHighlightRange(
  content: string,
  symbols: Record<string, SymbolEntry>,
  filePath: string,
  highlightSymbol: string | undefined,
): Set<number> {
  if (!highlightSymbol) return new Set();
  const entry = symbols[highlightSymbol];
  if (!entry || entry.file !== filePath) return new Set();

  const lines = content.split('\n');
  const startLine = entry.line; // 1-based
  const highlighted = new Set<number>();
  let depth = 0;
  let started = false;

  for (let i = startLine - 1; i < lines.length; i++) {
    const line = lines[i];
    highlighted.add(i + 1);

    for (const ch of line) {
      if (ch === '{') { depth++; started = true; }
      if (ch === '}') depth--;
    }

    if (started && depth <= 0) break;
    // If no braces on the definition line (e.g. single-line enum value), just highlight that line
    if (i === startLine - 1 && !started && !line.includes('{')) break;
  }

  return highlighted;
}

export function ProtoViewer({
  content,
  filePath,
  symbols,
  currentPackage,
  highlightSymbol,
  showOnlyHighlighted,
}: ProtoViewerProps) {
  const classes = useStyles();

  const lineAnchors = useMemo(
    () => buildLineAnchors(symbols, filePath),
    [symbols, filePath],
  );

  const highlightedLines = useMemo(
    () => computeHighlightRange(content, symbols, filePath, highlightSymbol),
    [content, symbols, filePath, highlightSymbol],
  );

  const lines = useMemo(() => content.split('\n'), [content]);

  const tokenizedLines = useMemo(() => {
    let inBlockComment = false;
    return lines.map(line => {
      const result = tokenizeLine(line, currentPackage, symbols, inBlockComment);
      inBlockComment = result.stillInBlockComment;
      return result.tokens;
    });
  }, [lines, currentPackage, symbols]);

  const renderToken = (token: Token, idx: number) => {
    if (token.type === 'typeRef' && token.targetFile) {
      return (
        <RouterLink
          key={idx}
          className={classes.typeRef}
          to={`/proto-browser/${token.targetFile}#${token.fqn}`}
        >
          {token.value}
        </RouterLink>
      );
    }

    if (token.type === 'importPath' && token.targetFile) {
      const inner = token.value.slice(1, -1);
      const quote = token.value[0];
      return (
        <span key={idx} className={classes.string}>
          {quote}
          <RouterLink
            className={classes.typeRef}
            to={`/proto-browser/${token.targetFile}`}
          >
            {inner}
          </RouterLink>
          {quote}
        </span>
      );
    }

    const classMap: Record<string, string> = {
      keyword: classes.keyword,
      primitive: classes.primitive,
      string: classes.string,
      comment: classes.comment,
      number: classes.number,
      punctuation: classes.punctuation,
      fieldName: classes.fieldName,
    };

    const className = classMap[token.type];
    if (className) {
      return (
        <span key={idx} className={className}>
          {token.value}
        </span>
      );
    }

    return <Fragment key={idx}>{token.value}</Fragment>;
  };

  return (
    <pre className={classes.root}>
      <code>
        {tokenizedLines.map((tokens, lineIdx) => {
          const lineNum = lineIdx + 1;
          if (showOnlyHighlighted && highlightedLines.size > 0 && !highlightedLines.has(lineNum)) {
            return null;
          }
          const anchor = lineAnchors.get(lineNum);
          return (
            <div
              key={lineNum}
              id={anchor || undefined}
              className={highlightedLines.has(lineNum) ? classes.lineHighlighted : classes.line}
            >
              <span className={classes.lineNumber}>{lineNum}</span>
              {tokens.map(renderToken)}
              {'\n'}
            </div>
          );
        })}
      </code>
    </pre>
  );
}
